'use client';

import BatchStartCountdown from '@/app/[domain]/[locale]/courses/components/course-card/batch-start-countdown';
import { FilePathUtils } from '@/config';
import NumberFormatter from '@/utils/bn';
import { fallbackImages } from '@/utils/fallback-images';
import {
  BatchViewModel,
  CommonCourseUtils,
  CourseViewModel,
  CurrencyInfo,
} from '@hidayahlab/academy-suite-corelib';
import { Clock01Icon, UserGroupIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { BatchBadge } from '../batch-badge/batch-badge';
import CoursePricing from './course-pricing';

type CourseCardLiveDetailsProps = {
  course: CourseViewModel;
  batch: BatchViewModel;
  currencyInfo: CurrencyInfo;
};

export default function LiveCourseCardDetails({
  course,
  batch,
  currencyInfo,
}: CourseCardLiveDetailsProps) {
  const locale = useLocale();
  const t = useTranslations();

  const discountPrice = CommonCourseUtils.calculateDiscountedPrice(
    course.price,
    course.discount,
    course.discountType || 'PERCENTAGE',
  );

  return (
    <div className=" bg-white border border-gray-200 cursor-pointer flex flex-col gap-2 justify-between hover:scale-[103%] hover:shadow-lg hover:border hover:border-primary-500 transition-all duration-300 rounded-md relative h-full overflow-hidden shadow-md">
      <div className="bg-[#FA4542] flex items-center gap-1 px-2 py-1 rounded-xs absolute top-4 right-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="9"
          height="8"
          fill="none"
          viewBox="0 0 9 8"
        >
          <circle cx="4.5" cy="4" r="4" fill="#fff"></circle>
        </svg>
        <p className="text-sm text-white">Live</p>
      </div>

      <div>
        <Image
          src={
            course.thumbnail
              ? `${FilePathUtils.courseImagePath(course.id)}/${course.thumbnail}`
              : fallbackImages.course
          }
          alt={course.title}
          width={500}
          height={500}
          className="w-full aspect-[1600/960] object-cover"
        />
        <div className="p-4">
          <div className="flex justify-between">
            {batch.seats > 1 && (
              <div className="flex gap-2 items-center p-2 bg-gray-100 rounded-xs">
                <HugeiconsIcon
                  icon={UserGroupIcon}
                  className="text-gray-700 size-4"
                />
                <p className="text-sm text-gray-700">
                  {t('course-details.courseBenefitsCard.total-seats')}{' '}
                  {locale === 'bn' ? NumberFormatter(batch.seats) : batch.seats}
                </p>
              </div>
            )}
            <div className="flex gap-2 items-center p-2 bg-gray-100 rounded-xs">
              <HugeiconsIcon
                icon={Clock01Icon}
                className="text-gray-700 size-4"
              />
              <BatchStartCountdown
                courseCardView
                batchStartDate={batch.startDate}
              />
            </div>
          </div>
          <h3 className="py-3 text-lg font-bold text-gray-700">
            {course.title}
          </h3>
        </div>
      </div>
      <div className="flex justify-between items-center px-4 pb-4">
        {batch.status === 'UPCOMING' ? (
          <div className="inline gap-2 items-center p-1 px-2 text-sm text-white whitespace-nowrap rounded-sm bg-primary-500 w-fit">
            {t('course-card-live.status.upcoming')}
          </div>
        ) : (
          <CoursePricing
            price={course.price}
            discountPrice={discountPrice}
            discount={course.discount}
            isFree={course.isFree}
            currencyInfo={currencyInfo}
          />
        )}
        {<BatchBadge batch={batch} />}
      </div>
    </div>
  );
}
